import React from 'react'
import { Link } from 'react-router-dom'
import { ArrowUpRight } from 'lucide-react'
import { ChatToolCard } from '@/components'
import type { ToolCardStatus, ToolConfigEntry } from '@/components'
import { MESSAGE_STATUS } from '../../contexts/SSEEvents'
import type { MessageStatus } from '../api'

interface GenerateCodeToolData {
  toolName: string
  toolInput: {
    strategyDescription?: string
  }
  startedAt: string
  completedAt?: string
  canceledAt?: string
  status: 'running' | 'completed' | 'canceled'
  cid?: string
  version?: number
  codeId?: number
}

interface BotMessageToolGenerateCodeProps {
  toolData: GenerateCodeToolData
  status?: MessageStatus
}

export default function BotMessageToolGenerateCode({
  toolData,
  status,
}: BotMessageToolGenerateCodeProps) {
  const cardStatus = resolveStatus(status, toolData.status)
  const elapsedTime = useElapsedTime(toolData.startedAt, toolData.completedAt, cardStatus === 'running')

  const config = buildConfig(toolData, cardStatus)

  return (
    <ChatToolCard
      message={{
        id: 0,
        type: 'tool',
        label: labelFor('Generating Trading Code', cardStatus),
        status: cardStatus,
        duration: cardStatus === 'done' ? formatTime(elapsedTime) : undefined,
        config,
      }}
    />
  )
}

function labelFor(base: string, status: ToolCardStatus) {
  if (status === 'canceled') return `${base} (Canceled)`
  if (status === 'error') return `${base} (Failed)`
  return base
}

function buildConfig(
  toolData: GenerateCodeToolData,
  cardStatus: ToolCardStatus,
): ToolConfigEntry[] | undefined {
  const entries: ToolConfigEntry[] = []

  if (toolData.toolInput?.strategyDescription) {
    entries.push({ label: 'Description', value: toolData.toolInput.strategyDescription })
  }

  if (cardStatus === 'done' && (toolData.cid || toolData.version != null)) {
    entries.push({
      label: 'Version',
      value: (
        <VersionValue
          version={formatVersion(toolData.version)}
          to={toolData.cid ? `/code/${toolData.cid}` : undefined}
        />
      ),
    })
  }

  return entries.length > 0 ? entries : undefined
}

function resolveStatus(
  status: MessageStatus | undefined,
  toolStatus: GenerateCodeToolData['status'],
): ToolCardStatus {
  if (status === MESSAGE_STATUS.FAILED) return 'error'
  if (status === MESSAGE_STATUS.CANCELED || toolStatus === 'canceled') return 'canceled'
  if (status === MESSAGE_STATUS.PROCESSED || toolStatus === 'completed') return 'done'
  return 'running'
}

function useElapsedTime(startedAt: string | undefined, completedAt: string | undefined, isRunning: boolean) {
  const [tickElapsed, setTickElapsed] = React.useState(0)

  React.useEffect(() => {
    if (!startedAt || !isRunning) return
    const start = new Date(startedAt).getTime()
    const interval = setInterval(() => {
      setTickElapsed((Date.now() - start) / 1000)
    }, 100)
    return () => clearInterval(interval)
  }, [startedAt, isRunning])

  if (!isRunning && startedAt && completedAt) {
    const start = new Date(startedAt).getTime()
    const end = new Date(completedAt).getTime()
    return (end - start) / 1000
  }
  return tickElapsed
}

function formatTime(seconds: number) {
  return `${seconds.toFixed(1)}s`
}

function formatVersion(version?: number) {
  return version != null ? `v${version}` : null
}

function VersionValue({ version, to }: { version: string | null; to?: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      {version && <span className="font-mono tabular-nums">{version}</span>}
      {to && (
        <Link
          to={to}
          aria-label="Open code details"
          className="inline-flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowUpRight size={12} strokeWidth={1.75} />
        </Link>
      )}
    </span>
  )
}
